// ============================================================
// repriceRule.ts — WHEN the activity view offers "Re-price" (pure; tested in
// repriceRule.test.ts) + the current grade rates it compares against.
//
// Re-price = set an unbilled time entry's FROZEN hourly rate to the firm's
// CURRENT rate for the grade the work was done at (gradeAtEntry), and
// recompute its £ (6-minute units, rounded up). Admin-only on the server
// (POST /api/activities/reprice, requireAdmin), which also refuses billed
// entries (an issued bill sets billed: true) and audit-logs every change
// ('activity.repriced', rate + £ from → to, in the same transaction).
//
// The action only APPEARS when it would change something: an admin, a
// billable, unbilled, timed entry whose frozen rate differs from its grade's
// current (non-zero) rate. Otherwise it's hidden — no "nothing to change".
// ============================================================
import { listGradeRatesApi } from '../../api/casevoApi'
import type { GradeRate } from '../../types/grades.types'

export interface RepriceFacts {
  isAdmin: boolean
  billed: boolean
  billable: boolean // the entry's type bills time
  durationSeconds: number
  gradeAtEntry: string | null | undefined
  frozenRatePence: number | null | undefined // hourlyRatePenceAtEntry
  currentRates: Record<string, number> | null // grade → current hourly rate (pence)
}
export type RepriceOffer = { show: false } | { show: true; fromRatePence: number; toRatePence: number; hint: string }

// "£150/hr", "£201.10/hr".
export const perHour = (pence: number): string =>
  `£${(pence / 100).toLocaleString('en-GB', pence % 100 === 0 ? { maximumFractionDigits: 0 } : { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/hr`

export const repriceOffer = (f: RepriceFacts): RepriceOffer => {
  if (!f.isAdmin || f.billed || !f.billable || f.durationSeconds <= 0 || !f.gradeAtEntry || !f.currentRates) return { show: false }
  const now = f.currentRates[f.gradeAtEntry]
  // Unknown grade, or its rate is still £0 (the server never re-prices TO £0).
  if (now === undefined || now <= 0) return { show: false }
  const frozen = f.frozenRatePence ?? 0
  if (frozen === now) return { show: false }
  return { show: true, fromRatePence: frozen, toRatePence: now, hint: `Rate changed: ${perHour(frozen)} → ${perHour(now)}` }
}

// The firm's current grade rates (admin-only endpoint), shared across views
// and kept for a minute; Admin › Grade rates clears it after a change.
let cache: { at: number; rates: Promise<Record<string, number>> } | null = null
const TTL_MS = 60_000
export const loadCurrentGradeRates = (): Promise<Record<string, number>> => {
  if (cache && Date.now() - cache.at < TTL_MS) return cache.rates
  const rates = listGradeRatesApi().then((rows: GradeRate[]) => Object.fromEntries(rows.map((r) => [r.grade, r.hourlyRatePence])))
  cache = { at: Date.now(), rates }
  rates.catch(() => { cache = null })
  return rates
}
export const invalidateCurrentGradeRates = (): void => { cache = null }
